// 1.5.15: Uses shared/api client for BFF/legacy routing
import type { Restaurant } from '../../features/search/types/restaurant';
import type { StreamEvent } from '../../features/chat/types/chat';
import { logger } from '../utils/logger';
import {
  buildStreamSummary,
  createRequestId,
  createStreamMetrics,
  getStreamCounters,
  recordChunk,
  recordDone,
  recordMissingDone,
} from '../../features/chat/utils/streamMetrics';
import { normalizeStreamEvent } from '../../features/chat/utils/streamEventAdapter';
import { resolveStreamUrl, resolveUrl, buildHeaders } from '../api/client';

export { normalizeStreamEvent, resolveStreamUrl };

const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// 1.2.6: 开发环境调试 - 检查环境变量加载
if (import.meta.env.DEV) {
  console.log('🔍 Environment Variables Check:', {
    VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL || 'MISSING',
    VITE_SUPABASE_ANON_KEY: SUPABASE_ANON_KEY ? `${SUPABASE_ANON_KEY.substring(0, 20)}...` : 'MISSING',
    VITE_SUPABASE_PROJECT_REF: import.meta.env.VITE_SUPABASE_PROJECT_REF || 'MISSING',
    VITE_STREAM_PROVIDER: import.meta.env.VITE_STREAM_PROVIDER || 'legacy',
    VITE_BFF_URL: import.meta.env.VITE_BFF_URL || 'MISSING',
  });

  if (!SUPABASE_ANON_KEY) {
    console.error('❌ VITE_SUPABASE_ANON_KEY is not set!');
    console.error('   Please check .env.local file and restart the dev server.');
  }
}

// AI service URL helper (lazy, for non-stream requests only)
const getAiServiceUrl = (path: string) => resolveUrl('ai-service', path);

// Default AI configuration
const DEFAULT_AI_CONFIG = {
  restaurant_advisor: {
    model: 'gemini-2.5-flash',
    temperature: 0.8,
    maxTokens: 8000, // 1.2.7: 增加到 8000 以支持更详细的回复
    // 1.3.50: 修改语言规则 - 根据用户输入语言回复，而不是系统语言
    system_prompt: ({ language }: { language: string }) =>
      `You are a comprehensive restaurant recommendation assistant for Japan, specializing in Tokyo and Osaka regions. You can recommend restaurants across ALL cuisine types including but not limited to: Japanese cuisine (ramen, sushi, tempura, yakiniku, kaiseki), Chinese cuisine (Sichuan, Cantonese, dim sum), Italian cuisine (pasta, pizza, risotto), Indian cuisine (curry, tandoori, biryani), Vietnamese cuisine (pho, banh mi), French, Korean, Thai, and more. Analyze the user's mood, preferences, budget, and desired cuisine type to suggest suitable restaurants. Be concise and focus on matching their criteria across the entire culinary spectrum.

**LANGUAGE RULE**: For EACH message, detect the user's CURRENT input language and respond in THAT language.
- Japanese (has kana ひらがな/カタカナ): Respond in Japanese
- Chinese (has Chinese chars, no kana): Respond in Chinese  
- English (Latin alphabet like "shinjuku ramen"): Respond in **English**
- Korean (has Hangul): Respond in Korean
IGNORE conversation history and system language (${language}). Each response uses CURRENT message language only.`,
  },
  chat_advisor: {
    model: 'gemini-2.5-flash',
    temperature: 0.8,
    maxTokens: 8000, // 1.2.7: 增加到 8000 以支持更详细的回复
    system_prompt: ({ language }: { language: string }) =>
      // 1.3.18: 将餐厅和非餐饮场所的prompt分开，保证餐厅的prompt不变，其他类型的prompt单独写
      // 1.3.15: 扩展身份定义，支持通用场所推荐（与后端保持一致）
      // 1.3.14: `You are a warm and empathetic food recommendation assistant for Japan, specializing in Tokyo and Osaka regions. You understand various life situations and provide personalized restaurant recommendations.
      `You are a warm and empathetic location recommendation assistant for Japan, specializing in Tokyo and Osaka regions. You understand various life situations and provide personalized recommendations for restaurants, cafes, amusement parks, tourist attractions, parks, libraries, and other places. While you excel at restaurant recommendations, you also support recommendations for entertainment venues, sightseeing spots, family-friendly places, and more.

**==========================================
餐厅推荐规则（RESTAURANT RECOMMENDATION RULES）
==========================================**

🚨 CRITICAL MANDATORY RULE - FUNCTION CALLING (餐厅) 🚨
**ABSOLUTELY MANDATORY**: If you mention ANY specific restaurant name in your response (whether in Chinese, English, Japanese, or any language), you MUST immediately call the search_restaurants function BEFORE or WHILE writing your response. This is NOT optional - it is a strict requirement that cannot be ignored.

**HOW TO IDENTIFY RESTAURANT NAMES:**
A restaurant name is ANY text that refers to a specific establishment, including:
- Chinese restaurant names: "海底捞", "小肥羊", "呷哺呷哺"
- English restaurant names: "Haidilao", "Xiao Wei Yang", "Ichiran", "Bills", "Denny's", "McDonald's", "Starbucks", "TGI Friday's" (pure English names, even if uncommon like "Bills")
- Japanese restaurant names: "一蘭", "すき家", "焼肉陽山道", "焼肉上野太昌園", "焼肉ここから", "ビルズ", "デニーズ", "すしざんまい", "はま寿司"
- Names containing location markers: "上野本店", "渋谷店", "新宿店" (these indicate specific restaurant branches)
- Names with business type + name: "焼肉陽山道", "拉面一蘭", "火锅海底捞"
- ANY text that looks like a restaurant name (contains "店", "本店", specific location, or is a proper noun referring to a food establishment)
- **CRITICAL**: Even if an English name seems uncommon (like "Bills"), if it appears in a restaurant recommendation context, you MUST identify it as a restaurant name and call the function

**WHEN TO CALL search_restaurants (MANDATORY - 餐厅):**
- ✅ When you list specific restaurants (e.g., "烧肉阳山道 上野本店", "烧肉上野太昌园 本店", "烧肉ここから上野店")
- ✅ When you recommend a specific restaurant by name (e.g., "海底捞", "Haidilao", "小肥羊", "一蘭", "Ichiran", "焼肉陽山道")
- ✅ When you mention an English restaurant name (e.g., "Bills 表参道", "Bills Omotesando", "Denny's 渋谷")
- ✅ When you mention a Japanese restaurant name (e.g., "ビルズ 表参道", "デニーズ 渋谷")
- ✅ When you mention a restaurant name in your explanation or description
- ✅ When you discuss a specific restaurant the user asked about
- ✅ When you compare specific restaurants by name
- ✅ When you provide a numbered list of restaurants (e.g., "1. 烧肉阳山道...", "2. 烧肉上野太昌园...")
- ✅ ANY time a specific restaurant name appears in your response text, even if it's part of a longer description
- ✅ **CRITICAL**: If text appears in a restaurant recommendation, discussion, or comparison context and looks like a restaurant name, you MUST call the function

**WHEN NOT to call search_restaurants (餐厅):**
- ❌ When recommending general categories only (e.g., "拉面", "火锅", "烤肉" - food types, not restaurant names)
- ❌ When discussing cuisine types without mentioning specific restaurants (e.g., "我推荐日式烤肉" without naming specific restaurants)

**WHY THIS MATTERS (餐厅):**
- Calling search_restaurants will automatically display restaurant cards in the user interface
- Restaurant cards show: address, rating, photos, price range, opening hours, and Google Maps link
- Users can click on cards to get directions and see detailed information
- Without calling the function, users won't see the restaurant cards even if you mention the restaurant name
- This is a CRITICAL user experience requirement - missing function calls result in poor user experience

**IMPORTANT RULES (餐厅):**
1. **MANDATORY FUNCTION CALL**: Every time you mention a specific restaurant name in your response, you MUST call search_restaurants function immediately. This is a strict requirement, not a suggestion. NO EXCEPTIONS.
2. **For multiple restaurant recommendations**: If you list multiple restaurants (e.g., "1. 烧肉阳山道... 2. 烧肉上野太昌园... 3. 烧肉ここから..."), you MUST call search_restaurants for EACH restaurant separately. Each restaurant name mentioned requires its own function call.
3. **Call BEFORE or DURING response**: You should call the function as soon as you decide to mention a restaurant name, not after writing the entire response.
4. **Provide detailed descriptions** of restaurants including their specialties, atmosphere, and who they're suitable for.

**EXAMPLES OF CORRECT responses (餐厅 - MUST follow this pattern):**
- ✅ "上野有几家很不错的烤肉店哦! **烧肉阳山道 上野本店**..." → MUST call search_restaurants with keyword "烧肉阳山道 上野" or "焼肉陽山道 上野"
- ✅ "我推荐海底捞火锅，它非常适合..." → MUST call search_restaurants with keyword "海底捞"
- ✅ "小肥羊火锅也是一个很好的选择..." → MUST call search_restaurants with keyword "小肥羊"
- ✅ "一蘭拉面在东京很受欢迎..." → MUST call search_restaurants with keyword "一蘭"
- ✅ "烧肉上野太昌园 本店 (4.1星)..." → MUST call search_restaurants with keyword "烧肉上野太昌园" or "焼肉上野太昌園"
- ✅ "烧肉ここから上野店 (4.8星)..." → MUST call search_restaurants with keyword "烧肉ここから" or "焼肉ここから"
- ✅ "我推荐 **Bills 表参道**，它非常适合..." → MUST call search_restaurants with keyword "Bills 表参道" or "Bills Omotesando"
- ✅ "**Bills Omotesando** 是一个很好的选择..." → MUST call search_restaurants with keyword "Bills Omotesando"
- ✅ "**ビルズ 表参道** 的早餐很受欢迎..." → MUST call search_restaurants with keyword "ビルズ 表参道" or "Bills 表参道"
- ✅ "我推荐 **Denny's 渋谷店**..." → MUST call search_restaurants with keyword "Denny's 渋谷" or "デニーズ 渋谷"

**EXAMPLES OF INCORRECT responses (餐厅 - DO NOT DO THIS - THESE ARE WRONG):**
- ❌ "上野有几家很不错的烤肉店哦! **烧肉阳山道 上野本店** (4.2星)..." → WRONG: Mentioned "烧肉阳山道" but did NOT call search_restaurants
- ❌ "我推荐海底捞火锅..." → WRONG: Mentioned "海底捞" but did NOT call search_restaurants
- ❌ "小肥羊是一个很好的选择..." → WRONG: Mentioned "小肥羊" but did NOT call search_restaurants
- ❌ "我推荐 **Bills 表参道**..." → WRONG: Mentioned "Bills 表参道" but did NOT call search_restaurants
- ❌ "**ビルズ 表参道** 是一个很好的选择..." → WRONG: Mentioned "ビルズ 表参道" but did NOT call search_restaurants
- ❌ Listing multiple restaurants without calling function for each → WRONG: Must call function for each restaurant

**REMEMBER (餐厅)**: If you write a restaurant name in your response, you MUST call search_restaurants. There are NO exceptions to this rule. If you're unsure whether something is a restaurant name, err on the side of calling the function.

**==========================================
非餐饮场所推荐规则（NON-RESTAURANT PLACE RECOMMENDATION RULES）
==========================================**

**支持的场所类型**：
- 游乐场、主题公园（amusement_park）
- 景点（tourist_attraction）
- 公园（park）
- 图书馆（library）
- 博物馆（museum）
- 动物园（zoo）
- 水族馆（aquarium）
- 以及其他 Google Place 类型

🚨 CRITICAL MANDATORY RULE - FUNCTION CALLING (非餐饮场所) 🚨
**ABSOLUTELY MANDATORY**: If you mention ANY specific place name (amusement park, theme park, tourist attraction, etc.) in your response (whether in Chinese, English, Japanese, or any language), you MUST immediately call the search_restaurants function BEFORE or WHILE writing your response. This is NOT optional - it is a strict requirement that cannot be ignored.

**HOW TO IDENTIFY PLACE NAMES (非餐饮场所):**
A place name is ANY text that refers to a specific establishment, including:
- Chinese place names: "东京迪士尼乐园", "三丽鸥彩虹乐园", "读卖乐园", "上野公园"
- English place names: "Tokyo Disneyland", "Tokyo Disney Sea", "Ueno Park", "Sanrio Puroland"
- Japanese place names: "東京ディズニーランド", "三麗鷗彩虹樂園", "読売ランド", "上野公園"
- Names containing location markers: "上野公园", "新宿御苑"
- ANY text that looks like a place name (proper noun referring to a specific location)

**WHEN TO CALL search_restaurants (MANDATORY - 非餐饮场所):**
- ✅ When you list specific places (e.g., "东京迪士尼乐园", "东京迪士尼海洋", "三丽鸥彩虹乐园")
- ✅ When you recommend a specific place by name (e.g., "Tokyo Disneyland", "Tokyo Disney Sea", "Ueno Park")
- ✅ When you mention a place name in your explanation or description
- ✅ When you discuss a specific place the user asked about
- ✅ When you compare specific places by name
- ✅ When you provide a numbered list of places (e.g., "1. 东京迪士尼乐园...", "2. 东京迪士尼海洋...")
- ✅ ANY time a specific place name appears in your response text, even if it's part of a longer description
- ✅ **CRITICAL**: If the first search returns empty results but you mention specific place names in your response, you MUST call search_restaurants for EACH place name

**WHEN NOT to call search_restaurants (非餐饮场所):**
- ❌ When recommending general categories only (e.g., "主题公园", "景点", "公园" - place types, not specific place names)
- ❌ When discussing place types without mentioning specific places (e.g., "我推荐主题公园" without naming specific places)

**WHY THIS MATTERS (非餐饮场所):**
- Calling search_restaurants will automatically display place cards in the user interface
- Place cards show: address, rating, photos, opening hours, and Google Maps link
- Users can click on cards to get directions and see detailed information
- Without calling the function, users won't see the place cards even if you mention the place name
- This is a CRITICAL user experience requirement - missing function calls result in poor user experience

**IMPORTANT RULES (非餐饮场所):**
1. **MANDATORY FUNCTION CALL**: Every time you mention a specific place name in your response, you MUST call search_restaurants function immediately. This is a strict requirement, not a suggestion. NO EXCEPTIONS.
2. **For multiple place recommendations**: If you list multiple places (e.g., "1. 东京迪士尼乐园... 2. 东京迪士尼海洋... 3. 三丽鸥彩虹乐园..."), you MUST call search_restaurants for EACH place separately. Each place name mentioned requires its own function call.
3. **Call BEFORE or DURING response**: You should call the function as soon as you decide to mention a place name, not after writing the entire response.
4. **Provide detailed descriptions** of places including their features, atmosphere, and who they're suitable for.
5. **🚨 特别重要 - 搜索返回空结果时的处理（非餐饮场所）**：
   - If the first search returns empty results but you mention specific place names in your response (e.g., "东京迪士尼乐园", "三丽鸥彩虹乐园", "读卖乐园"), you MUST call search_restaurants for EACH place name
   - **ABSOLUTELY DO NOT** only generate text response without calling the function
   - Example: If user asks "东京有名的主题公园", the first search may return empty results, but if you mention "东京迪士尼乐园", "东京迪士尼海洋", "三丽鸥彩虹乐园", "读卖乐园" in your response, you MUST call the function for each:
     - search_restaurants(keyword: "Tokyo Disneyland", location: "东京")
     - search_restaurants(keyword: "Tokyo Disney Sea", location: "东京")
     - search_restaurants(keyword: "三丽鸥彩虹乐园", location: "东京")
     - search_restaurants(keyword: "读卖乐园", location: "东京")
   - This is a mandatory requirement with NO EXCEPTIONS

**EXAMPLES OF CORRECT responses (非餐饮场所 - MUST follow this pattern):**
- ✅ "东京有名的主题公园包括**东京迪士尼乐园**、**东京迪士尼海洋**、**三丽鸥彩虹乐园**和**读卖乐园**..." → MUST call search_restaurants for each:
  - search_restaurants(keyword: "Tokyo Disneyland", location: "东京")
  - search_restaurants(keyword: "Tokyo Disney Sea", location: "东京")
  - search_restaurants(keyword: "三丽鸥彩虹乐园", location: "东京")
  - search_restaurants(keyword: "读卖乐园", location: "东京")
- ✅ "上野附近有几个不错的景点：**上野公园**、**东京国立博物馆**..." → MUST call search_restaurants for each place

**EXAMPLES OF INCORRECT responses (非餐饮场所 - DO NOT DO THIS - THESE ARE WRONG):**
- ❌ "东京有名的主题公园包括东京迪士尼乐园、东京迪士尼海洋、三丽鸥彩虹乐园和读卖乐园..." → WRONG: Mentioned place names but did NOT call search_restaurants (this is ABSOLUTELY WRONG)
- ❌ Listing multiple places without calling function for each → WRONG: Must call function for each place

**REMEMBER (非餐饮场所)**: If you write a place name in your response, you MUST call search_restaurants. There are NO exceptions to this rule. If you're unsure whether something is a place name, err on the side of calling the function. If you mention specific place names but don't call the function, this is a SERIOUS ERROR.

Be conversational, empathetic, and provide detailed explanations. Always consider the user's emotional state and physical condition.

**==========================================
语言规则（LANGUAGE RULES - ABSOLUTE HIGHEST PRIORITY）
==========================================**

🚨🚨🚨 **RESPONSE LANGUAGE RULE** 🚨🚨🚨

**CRITICAL**: For EACH message, detect the language of the user's CURRENT input and respond in THAT EXACT language.

**Language Detection Rules**:
1. **Japanese** (日本語): Contains hiragana (ひらがな), katakana (カタカナ), or Japanese kanji context
   - Example: "新宿でラーメン" → Respond in Japanese
   
2. **Chinese** (中文): Contains Chinese characters WITHOUT Japanese kana
   - Example: "新宿有什么好吃的" → Respond in Chinese
   
3. **English**: Contains primarily Latin alphabet words
   - Example: "shinjuku izakaya" → Respond in **English**
   - Example: "best ramen in Tokyo" → Respond in **English**
   
4. **Korean** (한국어): Contains Hangul characters
   - Example: "신주쿠 라멘" → Respond in Korean

**IMPORTANT RULES**:
- **IGNORE conversation history language** - Only use CURRENT message language
- **IGNORE system language parameter** (${language}) - Only detect from user input
- **For romanized Japanese** (e.g., "shinjuku", "izakaya") in English alphabet → Respond in **English**
- **Each response is independent** - If user switches language, switch your response language too`,
  },
  image_analyzer: {
    model: 'gemini-2.5-flash',
    temperature: 0.7,
    maxTokens: 1000,
    system_prompt:
      'You are an expert food image analyst specializing in global cuisines. Identify the dish type, cuisine style (Japanese, Chinese, Italian, Indian, Vietnamese, French, Korean, Thai, etc.), and key characteristics from the uploaded image. Recognize both traditional Japanese dishes and international cuisines popular in Japan. Provide brief, structured analysis covering dish type, cuisine origin, and flavor profile.',
  },
};

// Compress image to reduce size for better performance
const compressImage = async (file: File, maxWidth: number = 1024): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, { type: 'image/jpeg' });
              resolve(compressedFile);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/jpeg',
          0.8
        );
      };
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Analyze food image using AI via Supabase Edge Function
export const analyzeFoodImage = async (imageFile: File) => {
  const startTime = Date.now();

  logger.log('🖼️ Starting image analysis:', {
    fileName: imageFile.name,
    fileSize: `${(imageFile.size / 1024).toFixed(2)}KB`,
    fileType: imageFile.type
  });

  const config = DEFAULT_AI_CONFIG.image_analyzer;

  try {
    // Compress image for better performance
    const compressedImage = await compressImage(imageFile);

    logger.log('🤖 AI API Request (Image Analysis):', {
      model: config.model,
      scene: 'image_analyzer',
      imageSize: 'compressed to 1024px max',
      parameters: {
        temperature: config.temperature || 0.7
      }
    });

    const formData = new FormData();
    formData.append('image', compressedImage);
    formData.append('config', JSON.stringify(config));

    const headers: HeadersInit = {
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    };

    const response = await fetch(getAiServiceUrl('/analyze-image'), {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      logger.error('❌ API Error - Image analysis failed:', errorData);
      throw new Error(`Image analysis failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    logger.log('✅ AI API Response (Image Analysis):', {
      model: config.model,
      scene: 'image_analyzer',
      result,
      processingTime: `${Date.now() - startTime}ms`
    });

    return result;
  } catch (error: any) {
    logger.error('❌ API Error - Image analysis failed:', {
      model: config.model,
      scene: 'image_analyzer',
      error: error.message,
      processingTime: `${Date.now() - startTime}ms`
    });
    throw new Error(`API Error - Image analysis failed: ${error.message}`);
  }
};

// Generate restaurant recommendations based on query via Supabase Edge Function
export const getRestaurantRecommendations = async (
  query: string,
  language: string = 'zh',
  imageAnalysis?: any
) => {
  const startTime = Date.now();

  logger.log('🍽️ Starting restaurant recommendation:', {
    query: query.substring(0, 100),
    language,
    hasImageAnalysis: !!imageAnalysis
  });

  const config = DEFAULT_AI_CONFIG.restaurant_advisor;

  // Handle system_prompt as either function or string
  const systemPrompt = typeof config.system_prompt === 'function'
    ? config.system_prompt({ language })
    : config.system_prompt || 'You are a restaurant recommendation assistant.';

  logger.log('🤖 AI API Request (Restaurant Recommendation):', {
    model: config.model,
    scene: 'restaurant_advisor',
    input: query.substring(0, 150) + '...',
    systemPrompt: systemPrompt.substring(0, 100) + '...',
    parameters: {
      temperature: config.temperature || 0.8,
      maxTokens: config.maxTokens || 2000
    }
  });

  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    };

    const response = await fetch(getAiServiceUrl('/restaurant-recommendations'), {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query,
        language,
        imageAnalysis,
        config: {
          ...config,
          system_prompt: systemPrompt,
        },
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      logger.error('❌ API Error - Restaurant recommendation failed:', errorData);
      throw new Error(`Restaurant recommendation failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    const text = data.text || '';

    logger.log('✅ AI API Response (Restaurant Recommendation):', {
      model: config.model,
      scene: 'restaurant_advisor',
      outputLength: text.length,
      responsePreview: text.substring(0, 200) + '...',
      processingTime: `${Date.now() - startTime}ms`
    });

    return text;
  } catch (error: any) {
    logger.error('❌ API Error - Restaurant recommendation failed:', {
      model: config.model,
      scene: 'restaurant_advisor',
      error: error.message,
      processingTime: `${Date.now() - startTime}ms`
    });
    throw new Error(`API Error - Restaurant recommendation failed: ${error.message}`);
  }
};

// Interface for recommendation data
export interface RecommendationData {
  recommendedTypes: string[];
  keywords?: string[];
  budget?: string;
  location?: string;
}

// Streaming restaurant recommendations with SSE
// 1.2.21: 更新onChunk回调，支持传递chunk标签信息
// 1.3.0: 添加userLocation参数支持GPS定位
// 1.6.0: 添加onProgress回调，用于展示Agent各节点处理进度
export const getStreamingRecommendations = async (
  query: string,
  language: string = 'zh',
  conversationHistory?: Array<{ role: string; content: string }>,
  onChunk?: (
    chunk: string,
    tag?: { sequence: number; timestamp: number; requestId?: string }
  ) => void, // 1.2.21: 添加tag参数
  onComplete?: (fullText: string) => void,
  onRecommendation?: (recommendation: RecommendationData) => void,
  // 1.3.8: 修改回调签名，支持同时接收 restaurantReasons（与 restaurants 按索引对齐）
  onRestaurants?: (restaurants: Restaurant[], restaurantReasons?: string[]) => void,
  // 1.3.9: 新增回调，用于更新餐厅推荐理由（替换而非追加）
  onRestaurantsUpdate?: (restaurantReasons: string[]) => void,
  onError?: (error: Error) => void,
  imageFile?: File,
  userLocation?: { lat: number; lng: number }, // 1.3.0: GPS位置参数
  requestId?: string, // 1.4.0: 观测 requestId
  // 1.6.0: progress 回调，用于展示 Agent 各节点处理进度（如"🔍 正在理解您的需求..."）
  onProgress?: (step: string, message: string) => void,
  // 1.6.0: 登录用户 ID — 传给后端用于 Mem0 记忆召回和个性化推荐
  authUserId?: string,
): Promise<string> => {
  const startTime = Date.now();
  // 1.4.0: 初始化流式观测
  const streamRequestId = requestId || createRequestId();
  const streamMetrics = createStreamMetrics(streamRequestId, startTime);
  let doneReceived = false;

  logger.log('💬 Starting streaming recommendation:', {
    requestId: streamRequestId,
    query: query.substring(0, 100),
    language,
    hasHistory: !!conversationHistory && conversationHistory.length > 0,
    hasImageFile: !!imageFile,
    imageFileName: imageFile?.name,
    hasUserLocation: !!userLocation, // 1.3.0: GPS位置
    userLocation: userLocation ? { lat: userLocation.lat, lng: userLocation.lng } : undefined
  });

  const config = DEFAULT_AI_CONFIG.chat_advisor;

  // Handle system_prompt as either function or string
  const systemPrompt = typeof config.system_prompt === 'function'
    ? config.system_prompt({ language })
    : config.system_prompt || 'You are a restaurant recommendation assistant.';

  let streamUrl = '';
  try {
    let headers: HeadersInit;
    let requestBody: FormData | string;
    streamUrl = resolveStreamUrl();

    if (imageFile) {
      // If image file is provided, send as FormData
      const formData = new FormData();
      formData.append('query', query);
      formData.append('language', language);
      if (conversationHistory) {
        formData.append('conversationHistory', JSON.stringify(conversationHistory));
      }
      formData.append('image', imageFile);
      formData.append('config', JSON.stringify({
        ...config,
        system_prompt: systemPrompt,
      }));
      // 1.3.0: 添加GPS位置参数
      if (userLocation) {
        formData.append('userLocation', JSON.stringify(userLocation));
      }
      // 1.6.0: 传递登录用户 ID，用于 Mem0 记忆召回
      if (authUserId) {
        formData.append('authUserId', authUserId);
      }

      headers = {
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        // Don't set Content-Type for FormData, browser will set it with boundary
      };

      requestBody = formData;

      // 1.2.6: 添加详细的认证调试信息
      logger.log('📤 Sending streaming request with image:', {
        requestId: streamRequestId,
        url: streamUrl,
        method: 'POST',
        hasAuth: !!SUPABASE_ANON_KEY,
        anonKeyLength: SUPABASE_ANON_KEY?.length || 0,
        anonKeyPrefix: SUPABASE_ANON_KEY?.substring(0, 20) || 'EMPTY',
        imageFileName: imageFile.name,
        imageSize: `${(imageFile.size / 1024).toFixed(2)}KB`,
      });

      // 1.2.6: 如果 anon key 为空，记录警告
      if (!SUPABASE_ANON_KEY || SUPABASE_ANON_KEY.length === 0) {
        logger.error('❌ VITE_SUPABASE_ANON_KEY is empty! Please check .env.local and restart the dev server.');
      }
    } else {
      // No image, send as JSON
      headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      };

      const jsonBody = {
        query,
        language,
        conversationHistory,
        config: {
          ...config,
          system_prompt: systemPrompt,
        },
        // 1.3.0: 添加GPS位置参数
        ...(userLocation && { userLocation }),
        // 1.6.0: 传递登录用户 ID，用于 Mem0 记忆召回和个性化推荐
        ...(authUserId && { authUserId }),
      };

      requestBody = JSON.stringify(jsonBody);

      // 1.2.6: 添加详细的认证调试信息
      logger.log('📤 Sending streaming request:', {
        requestId: streamRequestId,
        url: streamUrl,
        method: 'POST',
        hasAuth: !!SUPABASE_ANON_KEY,
        anonKeyLength: SUPABASE_ANON_KEY?.length || 0,
        anonKeyPrefix: SUPABASE_ANON_KEY?.substring(0, 20) || 'EMPTY',
        bodySize: requestBody.length,
      });

      // 1.2.6: 如果 anon key 为空，记录警告
      if (!SUPABASE_ANON_KEY || SUPABASE_ANON_KEY.length === 0) {
        logger.error('❌ VITE_SUPABASE_ANON_KEY is empty! Please check .env.local and restart the dev server.');
      }
    }

    // Add timeout to fetch request (60 seconds)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);

    let response: Response;
    try {
      response = await fetch(streamUrl, {
        method: 'POST',
        headers,
        body: requestBody,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
    } catch (fetchError: any) {
      clearTimeout(timeoutId);
      if (fetchError.name === 'AbortError') {
        const error = new Error('Request timeout after 60 seconds');
        logger.error('❌ Request timeout:', error);
        onError?.(error);
        throw error;
      }
      // Network error or other fetch error
      const error = new Error(`Network error: ${fetchError.message || 'Failed to fetch'}`);
      logger.error('❌ Network error:', {
        error: fetchError,
        message: fetchError.message,
        name: fetchError.name,
        stack: fetchError.stack,
      });
      onError?.(error);
      throw error;
    }

    if (!response.ok) {
      let errorData: any = {};
      try {
        const text = await response.text();
        errorData = text ? JSON.parse(text) : {};
      } catch (e) {
        // If parsing fails, use empty object
      }

      // 1.2.6: 添加详细的 401 错误调试信息
      if (response.status === 401) {
        const authHeader = headers['Authorization'] || headers['authorization'] || 'MISSING';
        logger.error('❌ 401 Unauthorized - 详细调试信息:', {
          status: response.status,
          statusText: response.statusText,
          url: streamUrl,
          hasAnonKey: !!SUPABASE_ANON_KEY,
          anonKeyLength: SUPABASE_ANON_KEY?.length || 0,
          anonKeyPrefix: SUPABASE_ANON_KEY?.substring(0, 30) || 'EMPTY',
          authHeaderPrefix: authHeader !== 'MISSING' ? authHeader.substring(0, 30) + '...' : 'MISSING',
          envVars: {
            VITE_SUPABASE_URL: SUPABASE_URL || 'MISSING',
            VITE_SUPABASE_ANON_KEY: SUPABASE_ANON_KEY ? 'SET' : 'MISSING',
            VITE_SUPABASE_PROJECT_REF: SUPABASE_PROJECT_REF || 'MISSING',
          },
          errorData,
        });

        // 提供明确的错误提示
        if (!SUPABASE_ANON_KEY || SUPABASE_ANON_KEY.length === 0) {
          logger.error('💡 解决方案: VITE_SUPABASE_ANON_KEY 未设置！请检查 .env.local 并重启开发服务器');
        } else {
          logger.error('💡 解决方案: 请检查 anon key 是否正确，运行: cd backend && supabase status');
        }
      }

      const errorMessage = errorData.message || errorData.error || `${response.status} ${response.statusText}`;
      const error = new Error(`Streaming failed: ${errorMessage}`);
      logger.error('❌ API Error - Streaming failed:', {
        status: response.status,
        statusText: response.statusText,
        url: streamUrl,
        errorData,
        fullError: error,
      });
      onError?.(error);
      throw error;
    }

    if (!response.body) {
      const error = new Error('Response body is null');
      onError?.(error);
      throw error;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = '';
    let buffer = '';

    const handleStreamEvent = (event: StreamEvent): boolean => {
      if (event.type === 'done') {
        // Stream completed
        recordDone(streamMetrics);
        doneReceived = true;
        const doneSummary = buildStreamSummary(streamMetrics, Date.now());
        const doneCounters = getStreamCounters();
        logger.log('✅ Streaming completed:', {
          requestId: streamRequestId,
          totalLength: fullText.length,
          processingTime: `${Date.now() - startTime}ms`,
          ...doneSummary,
          doneRate: doneCounters.total ? doneCounters.done / doneCounters.total : 0,
        });
        onComplete?.(fullText);
        return true;
      }

      if (event.type === 'error') {
        const error = new Error(event.payload.message || 'Stream error');
        logger.error('❌ Stream error:', {
          requestId: streamRequestId,
          message: event.payload.message,
        });
        onError?.(error);
        throw error;
      }

      // 1.6.0: 处理 progress 事件 — 展示 Agent 各节点的实时处理进度
      if (event.type === 'progress') {
        logger.log('📊 Progress:', event.payload);
        onProgress?.(event.payload.step, event.payload.message);
        return false;
      }

      if (event.type === 'recommendation') {
        logger.log('📋 Received recommendation data:', event.payload);
        onRecommendation?.(event.payload);
        return false;
      }

      if (event.type === 'tool_result') {
        const restaurantReasons = event.payload.restaurantReasons || [];
        if (event.payload.updated) {
          logger.log(`🔄 Received updated restaurant reasons (${restaurantReasons.length} reasons)`);
          if (onRestaurantsUpdate) {
            onRestaurantsUpdate(restaurantReasons);
          } else {
            console.error('❌ [aiService] onRestaurantsUpdate callback is undefined!');
          }
        } else {
          logger.log(`🍽️ Received ${event.payload.restaurants.length} restaurants with ${restaurantReasons.length} reasons from Function Calling`);
          onRestaurants?.(event.payload.restaurants, restaurantReasons);
        }
        return false;
      }

      if (event.type === 'chunk') {
        const chunkText = event.payload.text;
        const chunkTime = Date.now();
        recordChunk(streamMetrics, { sequence: event.sequence }, chunkTime);
        if (streamMetrics.chunkCount === 1) {
          logger.log('⏱️ First token delay:', {
            requestId: streamRequestId,
            firstTokenDelayMs: chunkTime - startTime,
            sequence: event.sequence,
          });
        }
        fullText += chunkText;

        const tag = event.sequence && event.timestamp
          ? {
            sequence: event.sequence,
            timestamp: event.timestamp,
            requestId: streamRequestId,
          }
          : undefined;

        if (chunkText) {
          onChunk?.(chunkText, tag);
        }

        // Log chunk details for debugging
        if (chunkText) {
          logger.log('📝 Received chunk:', {
            requestId: streamRequestId,
            chunkLength: chunkText.length,
            totalLength: fullText.length,
            chunkPreview: chunkText.substring(0, 50),
            sequence: event.sequence,
            timestamp: event.timestamp
          });
        }
      }

      return false;
    };

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        // Keep the last incomplete line in buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = normalizeStreamEvent(JSON.parse(line.slice(6)));
              if (!event) {
                continue;
              }
              const shouldReturn = handleStreamEvent(event);
              if (shouldReturn) {
                return fullText;
              }
            } catch (e) {
              // Skip invalid JSON lines
              logger.warn('Warning: Failed to parse SSE data:', e);
            }
          }
        }
      }

      // Handle any remaining buffer
      if (buffer.trim()) {
        const lines = buffer.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = normalizeStreamEvent(JSON.parse(line.slice(6)));
              if (!event) {
                continue;
              }
              const shouldReturn = handleStreamEvent(event);
              if (shouldReturn) {
                return fullText;
              }
            } catch (e) {
              logger.warn('Warning: Failed to parse final SSE data:', e);
            }
          }
        }
      }

      // 1.4.0: 记录未收到done事件的统计
      if (!doneReceived) {
        recordMissingDone(streamMetrics);
      }
      const finalSummary = buildStreamSummary(streamMetrics, Date.now());
      const finalCounters = getStreamCounters();
      logger.log('✅ Streaming completed:', {
        requestId: streamRequestId,
        totalLength: fullText.length,
        processingTime: `${Date.now() - startTime}ms`,
        finalPreview: fullText.substring(0, 100),
        doneReceived,
        ...finalSummary,
        doneRate: finalCounters.total ? finalCounters.done / finalCounters.total : 0,
      });
      onComplete?.(fullText);
      return fullText;
    } catch (error) {
      // 1.4.0: 记录异常导致的done缺失
      if (!doneReceived) {
        recordMissingDone(streamMetrics);
      }
      logger.error('❌ Stream reading error:', {
        requestId: streamRequestId,
        error,
      });
      const err = error instanceof Error ? error : new Error('Unknown stream error');
      onError?.(err);
      throw err;
    } finally {
      reader.releaseLock();
    }
  } catch (error: any) {
    // 1.4.0: 记录异常导致的done缺失（请求级别）
    if (!doneReceived) {
      recordMissingDone(streamMetrics);
    }
    logger.error('❌ API Error - Streaming failed:', {
      requestId: streamRequestId,
      error: error.message,
      errorName: error.name,
      errorStack: error.stack,
      url: streamUrl,
      processingTime: `${Date.now() - startTime}ms`,
    });
    const err = error instanceof Error ? error : new Error(`Unknown error: ${String(error)}`);
    onError?.(err);
    throw err;
  }
};

// Get specific restaurant names based on recommendation data
export interface RestaurantNamesResponse {
  restaurantNames: string[];
  cuisineTypes: string[];
  location: string;
}

export const getRestaurantNames = async (
  recommendation: RecommendationData,
  language: string = 'zh'
): Promise<RestaurantNamesResponse> => {
  const startTime = Date.now();

  logger.log('🍽️ Getting restaurant names:', {
    recommendedTypes: recommendation.recommendedTypes,
    keywords: recommendation.keywords,
    budget: recommendation.budget,
    location: recommendation.location,
    language
  });

  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    };

    const response = await fetch(getAiServiceUrl('/get-restaurant-names'), {
      method: 'POST',
      headers,
      body: JSON.stringify({
        recommendedTypes: recommendation.recommendedTypes,
        keywords: recommendation.keywords,
        budget: recommendation.budget,
        location: recommendation.location || 'Tokyo',
        language,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      logger.error('❌ API Error - Get restaurant names failed:', errorData);
      throw new Error(`Get restaurant names failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    logger.log('✅ Restaurant names received:', {
      restaurantCount: data.restaurantNames?.length || 0,
      restaurantNames: data.restaurantNames,
      processingTime: `${Date.now() - startTime}ms`
    });

    return data as RestaurantNamesResponse;
  } catch (error: any) {
    logger.error('❌ API Error - Get restaurant names failed:', {
      error: error.message,
      processingTime: `${Date.now() - startTime}ms`
    });
    throw new Error(`API Error - Get restaurant names failed: ${error.message}`);
  }
};
